import { useEffect, useState } from "react";
import { Button, Flex, Grid, TextField } from "@adobe/react-spectrum";
import PhoneInput, { getCountryCallingCode } from "react-phone-number-input";
import { BackspaceIcon, CallIcon, CrossIcon } from "../../common/Icons";
import Emitter from "../../../emitter";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/storeConfig";
import { API_CALL_SEND_DTMF } from "../../../constants/events";
import { makeCall } from "../../../utils/utils";

interface CallDialPadProps {
  callType: string;
  onClose?: () => void;
}

const CallDialPad = (props: CallDialPadProps) => {
  const { callType, onClose } = props;
  const call = useSelector((state: RootState) => state.call);
  const agentStatus = useSelector((state: RootState) => state.agent?.state?.id);
  const voicemailId = useSelector((state: RootState) => state.voicemail?.id);
  const callbackId = useSelector((state: RootState) => state.callback?.id);

  const { isVoiceNetworkAvailable: vpnStatus, ccpSessionState } = useSelector(
    (state: RootState) => state.webrtc
  );
  const dialCallInProgress = useSelector(
    (state: RootState) => state.call.dialCallInProgress
  );

  const [number, setNumber] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<string>("US");

  useEffect(() => {
    if (call.id != "") {
      setNumber("");
    }
  }, []);

  const renderDialer = (num: number) => {
    const displayNumMap: { [key: number]: any } = {
      10: "*",
      11: "0",
      12: "#",
      13: <BackspaceIcon />,
    };

    const displayNum = displayNumMap[num] ?? num;

    const handleBtnpress = (num: number) => {
      setNumber((prevNumber) => {
        const countryCode = selectedCountry
          ? getCountryCallingCode(selectedCountry as any)
          : "";
        if (prevNumber === "" && !call.id) prevNumber = "+" + countryCode;
        //handle E.164 format error
        if ((!call.id && num === 10) || (!call.id && num === 12)) {
          return prevNumber;
        }
        if (num === 13) {
          return prevNumber.slice(0, -1);
        } else if (num === 14) {
          return "";
        } else {
          if (call.id) {
            sendDtmf(displayNum);
          }
          return prevNumber + displayNum;
        }
      });
    };

    return (
      <button
        key={num}
        onClick={() => handleBtnpress(num)}
        className="dialer-btn"
      >
        {displayNum}
      </button>
    );
  };

  const sendDtmf = (dtmf: string) => {
    Emitter.emit(API_CALL_SEND_DTMF, {
      body: { digits: dtmf },
    });
  };

  return (
    <Flex alignItems={"center"} justifyContent={"center"} width={"100%"}>
      <Flex justifyContent={"center"} direction={"column"}>
        <Flex direction={"row"} width={"100%"}>
          {callType === "conference" || callType === "call" ? (
            <PhoneInput
              value={number}
              onChange={(value) => {
                // Ensure the number is properly formatted
                const formattedNumber = value?.startsWith("+")
                  ? value
                  : `+${value}`;
                setNumber(formattedNumber ?? "");
              }}
              international
              className={"call-input"}
              onCountryChange={(country: any) => setSelectedCountry(country)}
              defaultCountry={"US"}
              onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => {
                // Handle pasted numbers manually
                const pastedData = event.clipboardData.getData("text");
                const formattedPastedData = pastedData.startsWith("+")
                  ? pastedData
                  : `+${pastedData}`;
                setNumber(formattedPastedData);
                event.preventDefault();
              }}
            />
          ) : (
            <TextField
              width={"100%"}
              value={number}
              label=""
              UNSAFE_className="call-dialpad-input"
              onChange={(value: string) => {
                setNumber(value);
                sendDtmf(value.charAt(value.length - 1));
              }}
              aria-label="call dialpad input"
              isQuiet
            />
          )}
          <span onClick={() => setNumber("")} className="cross-btn">
            <CrossIcon color="#000" />
          </span>
        </Flex>
        <Flex justifyContent={"center"} marginTop={20} width={"100%"}>
          <Grid
            areas={["1 2 3", "4 5 6", "7 8 9", "* 0 #", "call"]}
            columns={["1fr", "1fr", "1fr"]}
            rows={["1fr", "1fr", "1fr", "1fr", "auto"]}
            alignItems="center"
            justifyItems="center"
            height="100%"
            width={"100%"}
            gap={0}
            rowGap={7}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((num) =>
              renderDialer(num)
            )}
            <Flex gridColumn={callType === "dtmf" ? "span 3" : "span 1"}>
              {renderDialer(13)}
            </Flex>
            {callType !== "dtmf" && (
              <Flex marginStart={"10px"} width={"100%"} gridColumn="span 2">
                <Button
                  onPress={() => {
                    makeCall({
                      number,
                      dialCallInProgress,
                      callType,
                    });
                    onClose?.();
                  }}
                  aria-label="call-dial-button"
                  UNSAFE_className="call-dial-button"
                  isDisabled={
                    !vpnStatus ||
                    voicemailId ||
                    callbackId ||
                    ccpSessionState !== 4 ||
                    agentStatus === "AfterCallWork"
                  }
                  variant={"accent"}
                  width={"calc(100% - 12px)"}
                  maxWidth={"188px"}
                  height={"100%"}
                  maxHeight={"48px"}
                  data-testid="call-dial-button"
                >
                  <CallIcon
                    fill={`${
                      !vpnStatus ||
                      voicemailId ||
                      callbackId ||
                      ccpSessionState !== 4 ||
                      agentStatus === "AfterCallWork"
                    } ? "RGB(177,177,177)" : "#FFFFFF"}`}
                  />
                  <span style={{ marginLeft: "10px" }}>Call</span>
                </Button>
              </Flex>
            )}
          </Grid>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default CallDialPad;
